import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  Typography,
  Chip,
  Paper,
  IconButton,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload,
  PictureAsPdf,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import PdfPreviewDialog from '../common/PdfPreviewer';

interface OrderDetail {
  id: string;
  quantity: number;
  amount: number;
  variation: string | null;
  productId: string;
  created: string;
  modified: string;
}

interface OrderUser {
  id: string;
  firstname: string | null;
  lastname: string | null;
  username: string;
  email: string;
  cellnumber: string;
  userstatus: string;
  created: string;
  modified: string;
}

interface OrderCity {
  id: string;
  name: string;
  status: number;
  created: string;
  modified: string;
}

interface Order {
  id: string;
  orderno: string;
  expecteddelivery: string | null;
  orderstatus: number;
  totalamount: number;
  inshoppingcart: number;
  deliverydate: string | null;
  createdby: string;
  modifiedby: string;
  date: string;
  customername: string | null;
  totalitems: number;
  description: string | null;
  paymentDetail: string | null;
  address: string | null;
  baseprice: number | null;
  tax: number | null;
  additionalcharge: number | null;
  additionalchargedetails: string | null;
  shippingprice: number | null;
  discount: number | null;
  paymentstatus: number;
  ordermedia: any | null;
  deletedAt: string | null;
  created: string;
  modified: string;
  masterdetailId: string;
  userId: string;
  categoryId: string | null;
  cityId: string;
  user: OrderUser;
  category: any | null;
  city: OrderCity;
  orderdetails: OrderDetail[];
  ordernotes: any[];
  productmedia: any[];
}

interface OrderViewModalProps {
  open: boolean;
  onClose: () => void;
  order: Order | null;
}

const OrderViewModal: React.FC<OrderViewModalProps> = ({ open, onClose, order }) => {
  // Hooks MUST be declared unconditionally
  const [pdfFiles, setPdfFiles] = useState<File[]>([]);
  const [pdfPreviews, setPdfPreviews] = useState<{ name: string; size: number; url: string }[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);

  const handlePdfInput = (files: FileList | null) => {
    if (!files) return;
    const valid = Array.from(files).filter(f => f.type === 'application/pdf');
    if (!valid.length) return;
    const newPreviews = valid.map(f => ({
      name: f.name,
      size: f.size,
      url: URL.createObjectURL(f),
    }));
    setPdfFiles(prev => [...prev, ...valid]);
    setPdfPreviews(prev => [...prev, ...newPreviews]);
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handlePdfInput(e.target.files);
    e.target.value = '';
  };

  const removePdf = (idx: number) => {
    setPdfPreviews(prev => {
      URL.revokeObjectURL(prev[idx].url);
      return prev.filter((_, i) => i !== idx);
    });
    setPdfFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const clearAll = () => {
    pdfPreviews.forEach(p => URL.revokeObjectURL(p.url));
    setPdfPreviews([]);
    setPdfFiles([]);
  };

  useEffect(() => {
    return () => {
      pdfPreviews.forEach(p => URL.revokeObjectURL(p.url));
    };
  }, [pdfPreviews]);

  const handleUploadVoucher = useCallback(() => {
    console.log('Uploading voucher PDFs', pdfFiles);
    // clearAll(); // uncomment after successful upload
  }, [pdfFiles]);

  // Drag & Drop
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    if (e.type === 'dragleave') setDragActive(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.length) handlePdfInput(e.dataTransfer.files);
  };

  // Helpers
  const getStatusText = (status: number) => {
    switch (status) {
      case 1: return 'Pending';
      case 2: return 'Completed';
      case 3: return 'Cancelled';
      default: return 'Unknown';
    }
  };
  const getStatusColor = (status: number) => {
    switch (status) {
      case 1: return 'warning';
      case 2: return 'success';
      case 3: return 'error';
      default: return 'default';
    }
  };
  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };
  const formatCurrency = (amount: number) => amount?.toLocaleString() || '0';

  const hasOrder = !!order;

  // NEW: unify close to also clear PDF state
  const handleClose = () => {
    setPreviewIndex(null);
    clearAll();          // revokes URLs & empties arrays
    onClose();
  };

  // Also clear automatically if parent sets open = false externally
  useEffect(() => {
    if (!open) {
      setPreviewIndex(null);
      clearAll();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={false}
      maxWidth={false} // Disable default maxWidth
      PaperProps={{
        sx: {
          bgcolor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
          p: 3,
          m: { xs: 1.5, sm: 4 },
          borderRadius: 3,
          width: { xs: '98vw', sm: '1100px', md: '1400px' }, // Increased width
          maxWidth: '98vw',
        },
      }}
    >
      <DialogTitle
        sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', pr:1, pl:3, py:1.5, bgcolor:'background.default', borderBottom:'1px solid #e2e8f0' }}
      >
        <Typography variant="h5" fontWeight={800}>Order Details</Typography>
        <IconButton onClick={handleClose} color="error"> {/* UPDATED */}
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          flex: 1,
          p: 0,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          overflow: 'hidden',
        }}
      >
        {/* LEFT SECTION */}
        <Box
          sx={{
            flex: 1.35,
            p: 3,
            overflowY: 'auto',
            borderRight: { md: '1px solid #e2e8f0' },
          }}
        >
          {!hasOrder && (
            <Typography variant="body2" color="text.secondary">
              Loading order...
            </Typography>
          )}

          {hasOrder && (
            <>
              {/* Header */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  pb: 2,
                  mb: 3,
                  borderBottom: '1px solid #e2e8f0',
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">Order No.</Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ color: 'primary.main' }}>
                    {order!.orderno}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: .5 }}>
                    Placed on {formatDate(order!.date)}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Chip
                    label={getStatusText(order!.orderstatus)}
                    color={getStatusColor(order!.orderstatus) as any}
                    size="medium"
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="h6" fontWeight={800} sx={{ color: 'info.main' }}>
                    {formatCurrency(order!.totalamount)} pts
                  </Typography>
                </Box>
              </Box>

              {/* Customer Information */}
              <Paper elevation={0} sx={{ p: 2.5, mb: 3, border: '1px solid #e2e8f0', borderRadius: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Customer Information</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 4, rowGap: 2 }}>
                  <InfoItem label="Name" value={order!.user?.username || order!.customername || '-'} />
                  <InfoItem label="Email" value={order!.user?.email || '-'} />
                  <InfoItem label="Phone" value={order!.user?.cellnumber || '-'} />
                  <InfoItem label="City" value={order!.city?.name || '-'} />
                </Box>
              </Paper>

              {/* Order Items */}
              <Paper elevation={0} sx={{ p: 2.5, mb: 3, border: '1px solid #e2e8f0', borderRadius: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
                  Order Items ({order!.totalitems})
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                  {order!.orderdetails?.map(detail => (
                    <Box
                      key={detail.id}
                      sx={{
                        p: 1.5,
                        border: '1px solid #e2e8f0',
                        borderRadius: 1.5,
                        backgroundColor: '#f8fafc'
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary">Product ID</Typography>
                          <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-all' }}>
                            {detail.productId}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" color="text.secondary">Qty</Typography>
                          <Typography variant="body2" fontWeight={600}>{detail.quantity}</Typography>
                          <Typography variant="body2" fontWeight={600} sx={{ color: 'primary.main' }}>
                            {formatCurrency(detail.amount)} pts
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Paper>

              {/* Payment */}
              <Paper elevation={0} sx={{ p: 2.5, mb: 3, border: '1px solid #e2e8f0', borderRadius: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Payment</Typography>
                <Typography variant="body2">
                  Status: {order!.paymentstatus === 1 ? 'Paid' : 'Unpaid'}
                </Typography>
                {order!.paymentDetail && (
                  <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                    {order!.paymentDetail}
                  </Typography>
                )}
              </Paper>
            </>
          )}
        </Box>

        {/* RIGHT SECTION - Voucher PDFs */}
        <Box
          sx={{
            flex: 1,
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            overflowY: 'auto',
          }}
        >
          <Typography variant="h6" fontWeight={800}>
            Voucher PDFs
          </Typography>

            <Paper
              elevation={0}
              onDragEnter={handleDrag}
              onDragOver={handleDrag}
              onDragLeave={handleDrag}
              onDrop={handleDrop}
              sx={{
                p: 2.5,
                border: '1px dashed',
                borderColor: dragActive ? 'primary.main' : '#cbd5e1',
                borderRadius: 2,
                bgcolor: dragActive ? 'primary.lighter' : '#f8fafc',
                textAlign: 'center',
                transition: 'all .2s',
              }}
            >
              <input
                id="voucher-pdf-input"
                type="file"
                accept="application/pdf"
                multiple
                hidden
                onChange={onFileChange}
              />
              <CloudUpload sx={{ fontSize: 44, color: 'primary.main', mb: 1 }} />
              <Typography variant="body2" sx={{ mb: 1 }}>
                Drag & drop or click to select PDF files
              </Typography>
              <Button
                variant="contained"
                size="small"
                onClick={() => document.getElementById('voucher-pdf-input')?.click()}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Select PDFs
              </Button>
            </Paper>

          {pdfPreviews.length > 0 && (
            <Paper elevation={0} sx={{ p: 2, border: '1px solid #e2e8f0', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>
                  Selected Files ({pdfPreviews.length})
                </Typography>
                <Tooltip title="Clear all">
                  <Button
                    size="small"
                    color="error"
                    onClick={clearAll}
                    sx={{ textTransform: 'none' }}
                  >
                    Clear
                  </Button>
                </Tooltip>
              </Box>
              <Divider sx={{ mb: 1.5 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {pdfPreviews.map((pdf, idx) => (
                  <Paper
                    key={pdf.url}
                    variant="outlined"
                    sx={{
                      p: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      borderRadius: 1.5,
                    }}
                  >
                    <PictureAsPdf color="error" sx={{ fontSize: 30 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ wordBreak: 'break-all', lineHeight: 1.2 }}
                      >
                        {pdf.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {(pdf.size / 1024).toFixed(1)} KB
                      </Typography>
                    </Box>
                    <Tooltip title="Preview">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => setPreviewIndex(idx)}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removePdf(idx)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Paper>
                ))}
              </Box>
              {/* NOTE: If you want to limit visible rows to first 5:
                  {pdfPreviews.slice(0,5).map(...)}
                  and optionally show "+X more" */}
            </Paper>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 'auto' }}>
            <Button
              variant="contained"
              color="primary"
              disabled={!pdfFiles.length}
              onClick={handleUploadVoucher}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Upload Voucher
            </Button>
          </Box>
        </Box>
      </DialogContent>

      <PdfPreviewDialog
        open={previewIndex !== null}
        file={previewIndex !== null ? { name: pdfPreviews[previewIndex].name, url: pdfPreviews[previewIndex].url } : undefined}
        onClose={() => setPreviewIndex(null)}
        onRemove={
          previewIndex !== null
            ? () => {
                removePdf(previewIndex);
                setPreviewIndex(null);
              }
            : undefined
        }
      />
    </Dialog>
  );
};

const InfoItem: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ minWidth: '45%' }}>
    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600}>{value}</Typography>
  </Box>
);

export default OrderViewModal;